import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';

import * as Api from '../api';
import * as Mappers from '../mappers';
import { loadAccessContext } from '../access';
import { useAuthContext } from '../context';
import * as OrganizationApi from 'modules/organization';
import * as TeamsApi from 'modules/teams/api';
import config from 'config';
import { toast } from 'components/ui/use-toast';

export const useProfile = () => {
  const { state, methods } = useAuthContext();

  const { data, isSuccess, isError, isFetched: queryFetched, error, ...args } = useQuery({
    queryKey: ['auth', 'profile'],
    queryFn: async () => {
      const { data } = await Api.Profile();
      return data;
    },
    // Only run when authenticated and profile not yet fetched
    enabled: state.isAuthenticated && state.isInitialized && !state.isFetched,
    retry: false,
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // On success: update user/org/role/organizations and mark auth as fetched.
  // Only runs when isFetched is false (initial load / page refresh).
  // After org switch, isFetched stays true so this won't overwrite the fresh
  // context that handleSwitchOrg just set with stale cached react-query data.
  React.useEffect(() => {
    if (data && !state.isFetched) {
      methods.setUser(Mappers.User(data.user));

      const orgs = (data.organizations || []).map((o: any) => ({
        ...Mappers.Organization(o),
        role: o.role,
      }));
      methods.setOrganizations(orgs);

      // Use the org resolved by the backend (respects X-Organization-Id header)
      const activeOrg = data.organization;
      methods.setOrganization(activeOrg ? Mappers.Organization(activeOrg) : null);
      methods.setRole(data.role);

      // Persist active org FIRST so the X-Organization-Id header points at it,
      // then source permissions + entitlements from their dedicated endpoints
      // (they no longer ride along on the profile payload). Fire-and-forget: the
      // guards read empty access for a beat, then it populates.
      if (activeOrg) {
        localStorage.setItem(config.api.active_org_key, activeOrg.id);
        void loadAccessContext(methods);
      } else {
        methods.setPermissions([]);
        methods.setEntitlements(null);
        methods.setAccessFetched(true);
      }

      methods.setIsFetched(true);
    }
  }, [data, state.isFetched, methods]);

  // After ANY authentication, apply a pending team-join or invite (one-time).
  React.useEffect(() => {
    if (!data || !state.isFetched) return;

    // Team-join: existing-account login paths (e.g. Google) don't carry the join
    // token through to the backend, so finish the join here — mirrors the invite
    // flow below. Idempotent: re-joining as an existing member is a no-op. Remove
    // the token before the async call so a reload can't loop. On success, switch
    // into the joined org and hard-reload so the experience guard routes to the
    // right shell with the joined class visible.
    const teamJoinToken = sessionStorage.getItem('pending_team_join_token');
    if (teamJoinToken) {
      sessionStorage.removeItem('pending_team_join_token');
      TeamsApi.joinByToken(teamJoinToken)
        .then((response) => {
          const orgId = response?.data?.data?.organizationId;
          if (orgId) localStorage.setItem(config.api.active_org_key, orgId);
          window.location.href = '/';
        })
        .catch(() => {
          // Invalid/expired token — don't block the session; the user is in.
        });
      return;
    }

    const pendingInvite = sessionStorage.getItem('pending_invite_token');
    if (pendingInvite) {
      sessionStorage.removeItem('pending_invite_token');
      OrganizationApi.Api.AcceptInvitation(pendingInvite)
        .then(() => {
          // Refetch profile to get updated organizations
          window.location.href = '/app';
        })
        .catch(() => {
          // Invitation may have been already accepted or expired
        });
      return;
    }

    const pendingOrgInvite = sessionStorage.getItem('pending_org_invite_code');
    if (pendingOrgInvite) {
      sessionStorage.removeItem('pending_org_invite_code');
      OrganizationApi.Api.JoinByInvite(pendingOrgInvite)
        .then((response: any) => {
          const orgId = response?.data?.organization?.id || response?.data?.organization?._id;
          if (orgId) localStorage.setItem(config.api.active_org_key, orgId);
          window.location.href = '/app';
        })
        .catch((err: any) => {
          const msg =
            err?.response?.data?.message ||
            err?.message ||
            'Failed to join organization';
          toast.error(msg);
        });
      return;
    }
  }, [data, state.isFetched]);

  // On 401 error ONLY: logout (token invalid/expired)
  // Don't logout on network errors or other failures - user might just have connectivity issues
  React.useEffect(() => {
    if (isError && state.isAuthenticated && error) {
      const axiosError = error as AxiosError;
      // Only logout if we got a 401 Unauthorized response
      // The http interceptor handles token refresh, so if we still get 401, the refresh also failed
      if (axiosError.response?.status === 401) {
        methods.logout();
      }
    }
  }, [isError, state.isAuthenticated, error, methods]);

  // When profile query settles (success or non-401 error), mark isFetched so sidebar/menu can render
  React.useEffect(() => {
    if (state.isAuthenticated && queryFetched && !state.isFetched) {
      const axiosError = error as AxiosError | null;
      // Don't mark as fetched if it was a 401 (we're logging out)
      if (!isError || axiosError?.response?.status !== 401) {
        methods.setIsFetched(true);
      }
    }
  }, [state.isAuthenticated, queryFetched, state.isFetched, isError, error, methods]);

  return { data, isSuccess, isError, isFetched: queryFetched, error, ...args };
};
